import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { MONITORING, MONITORING_TIMING } from "@markedesk/core-contracts";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  applyLostPing,
  applyPong,
  classifyQuality,
  initialMetrics
} from "./metrics";

// Os números vêm do contrato, não de uma cópia local: o backend emite o
// batimento no MESMO intervalo, e antes disso cada lado declarava o seu com um
// comentário pedindo para não divergirem. Quem aumentasse só no servidor faria
// este hook concluir que o tempo-real travou e acender alarme falso na navbar.
const HEARTBEAT_MS = MONITORING_TIMING.heartbeatMs;
// Sem heartbeat por mais de ~2,5 intervalos → considera o tempo-real travado.
const STALE_AFTER_MS = MONITORING_TIMING.staleAfterMs;

// Intervalo entre pings de medição.
const PING_EVERY_MS = MONITORING_TIMING.pingEveryMs;
// Prazo para o pong voltar. Acima disso a resposta não é mais informação útil
// de latência — é perda.
const PING_TIMEOUT_MS = MONITORING_TIMING.pingTimeoutMs;

/**
 * Monitora o WebSocket e mede a latência do tempo-real.
 *
 * A medida é o RTT de um ping com ACK: o front carimba a saída, o backend
 * responde, o front carimba a chegada. Os dois carimbos saem do MESMO relógio,
 * então o número não depende de o relógio do servidor bater com o do
 * navegador — ao contrário da estimativa one-way que este hook fazia antes
 * (`Date.now() - serverTime` com desconto de clock skew pelo menor valor visto),
 * que mentia sempre que a máquina do atendente estava com a hora errada.
 *
 * O backend devolve no pong quanto tempo gastou processando e o atraso do seu
 * event loop, o que permite separar "internet do atendente" de "backend
 * saturado" — as duas produzem RTT alto e pedem ações opostas.
 *
 * Retorna { status, latency, metrics, quality }:
 *  - status: "connected" | "connecting" | "disconnected"
 *  - latency: RTT em ms (null se ainda não medido) — compatível com quem já usava
 *  - metrics: { rtt, networkMs, serverMs, jitter, loopLag, lost, lostStreak, samples, reconnects }
 *  - quality: "good" | "fair" | "bad" | "unknown"
 */
const useSocketStatus = () => {
  // Usa o MESMO socket que o app inteiro (AuthContext) — é nele que os eventos,
  // incluindo o server:heartbeat, chegam. (O socketManager.GetSocket() poderia
  // devolver outra instância e o hook nunca veria o heartbeat.)
  const { socket } = useContext(AuthContext);
  const [status, setStatus] = useState("connecting");
  const [metrics, setMetrics] = useState(initialMetrics);
  const lastBeatRef = useRef(0);
  const staleTimer = useRef(null);
  const pingTimer = useRef(null);
  // Timeouts em voo, para limpar no unmount e não chamar setState depois.
  const pendingRef = useRef(new Set());
  // O primeiro "connect" é a conexão inicial, não uma reconexão.
  const hadConnectedRef = useRef(false);

  const sendPing = useCallback(() => {
    if (!socket) return;

    const sentAt = Date.now();
    let settled = false;

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      pendingRef.current.delete(timeout);
      setMetrics(applyLostPing);
    }, PING_TIMEOUT_MS);

    pendingRef.current.add(timeout);

    socket.emit(MONITORING.PING, { t0: sentAt }, pong => {
      if (settled) return; // chegou depois do prazo: já contou como perdido
      settled = true;
      clearTimeout(timeout);
      pendingRef.current.delete(timeout);
      setMetrics(prev => applyPong(prev, { sentAt, receivedAt: Date.now(), pong }));
    });
  }, [socket]);

  useEffect(() => {
    if (!socket) return undefined;

    // Copiado para o escopo do efeito: o cleanup precisa limpar ESTE conjunto,
    // e ler `pendingRef.current` lá dentro leria o valor do momento da limpeza.
    const pending = pendingRef.current;

    const onConnect = () => {
      setStatus("connected");
      if (hadConnectedRef.current) {
        setMetrics(prev => ({ ...prev, reconnects: (prev.reconnects ?? 0) + 1 }));
      }
      hadConnectedRef.current = true;
      sendPing(); // mede logo na volta, sem esperar o próximo tick
    };

    const onDisconnect = () => {
      setStatus("disconnected");
      setMetrics(prev => ({ ...prev, rtt: null, networkMs: null, serverMs: null }));
    };

    const onHeartbeat = payload => {
      if (typeof payload?.serverTime !== "number") return;
      lastBeatRef.current = Date.now();
      setStatus("connected");
    };

    // O wrapper SocketWorker expõe só on/off/emit (não .io nem .connected).
    // O próprio heartbeat é o sinal de vida: se chega, está conectado.
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on(MONITORING.HEARTBEAT, onHeartbeat);

    // Vigia se o heartbeat parou de chegar (tempo-real travado). O ping mede
    // latência; o heartbeat é quem denuncia silêncio total do servidor.
    staleTimer.current = setInterval(() => {
      if (!lastBeatRef.current) return;
      if (Date.now() - lastBeatRef.current > STALE_AFTER_MS) {
        setStatus("connecting");
      }
    }, HEARTBEAT_MS);

    sendPing();
    pingTimer.current = setInterval(sendPing, PING_EVERY_MS);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off(MONITORING.HEARTBEAT, onHeartbeat);
      clearInterval(staleTimer.current);
      clearInterval(pingTimer.current);
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, [socket, sendPing]);

  return {
    status,
    latency: metrics.rtt,
    metrics,
    quality: classifyQuality(metrics)
  };
};

export default useSocketStatus;
